import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Can from '../../components/Can';
import { ROUTES } from '../../config/routes';
import crmContactoService from '../../services/crmContactoService';
import crmEmpresaService from '../../services/crmEmpresaService';
import type { CrmContactoListItem, CrmEmpresa } from '../../types/crm';
import { getApiErrorMessage } from '../../utils/apiError';

const emptyForm = {
  nombre: '',
  cuit: '',
  telefono: '',
  email: '',
  direccion: '',
  sitioWeb: '',
  nroCliente: '',
  notas: '',
  activo: true,
};

const CrmEmpresaForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'nuevo';
  const empresaId = isNew ? null : Number(id);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [form, setForm] = useState(emptyForm);
  const [detalle, setDetalle] = useState<CrmEmpresa | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [asociarOpen, setAsociarOpen] = useState(false);
  const [contactoOptions, setContactoOptions] = useState<CrmContactoListItem[]>([]);
  const [contactosSeleccionados, setContactosSeleccionados] = useState<CrmContactoListItem[]>([]);
  const [busquedaContacto, setBusquedaContacto] = useState('');
  const [loadingContactos, setLoadingContactos] = useState(false);
  const [asociando, setAsociando] = useState(false);

  const loadDetalle = async (eid: number) => {
    const e = await crmEmpresaService.getById(eid);
    setDetalle(e);
    setForm({
      nombre: e.nombre ?? '',
      cuit: e.cuit ?? '',
      telefono: e.telefono ?? '',
      email: e.email ?? '',
      direccion: e.direccion ?? '',
      sitioWeb: e.sitioWeb ?? '',
      nroCliente: e.nroCliente != null ? String(e.nroCliente) : '',
      notas: e.notas ?? '',
      activo: e.activo,
    });
  };

  useEffect(() => {
    if (empresaId == null || Number.isNaN(empresaId)) return;
    (async () => {
      try {
        setLoading(true);
        await loadDetalle(empresaId);
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar la empresa'), { variant: 'error' });
        navigate(ROUTES.crm.empresas.list);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId]);

  useEffect(() => {
    if (!asociarOpen) return;
    const timeoutId = setTimeout(async () => {
      try {
        setLoadingContactos(true);
        const data = await crmContactoService.getAll({
          searchTerm: busquedaContacto || undefined,
          pageNumber: 1,
          pageSize: 30,
          activo: true,
        });
        const asociados = new Set((detalle?.contactos ?? []).map((c) => c.id));
        setContactoOptions((data.items ?? []).filter((c) => !asociados.has(c.id)));
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, 'Error al buscar contactos'), { variant: 'error' });
        setContactoOptions([]);
      } finally {
        setLoadingContactos(false);
      }
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [asociarOpen, busquedaContacto, detalle?.contactos, enqueueSnackbar]);

  const handleSave = async () => {
    if (!form.nombre.trim()) {
      enqueueSnackbar('El nombre es obligatorio', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre.trim(),
        cuit: form.cuit.trim() || null,
        telefono: form.telefono.trim() || null,
        email: form.email.trim() || null,
        direccion: form.direccion.trim() || null,
        sitioWeb: form.sitioWeb.trim() || null,
        nroCliente: form.nroCliente.trim() ? Number(form.nroCliente) : null,
        notas: form.notas.trim() || null,
      };

      if (isNew) {
        const created = await crmEmpresaService.create(payload);
        enqueueSnackbar('Empresa creada', { variant: 'success' });
        navigate(ROUTES.crm.empresas.editar(created.id), { replace: true });
      } else if (empresaId != null) {
        await crmEmpresaService.update(empresaId, { ...payload, activo: form.activo });
        enqueueSnackbar('Empresa actualizada', { variant: 'success' });
        await loadDetalle(empresaId);
      }
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al guardar la empresa'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAsociar = async () => {
    if (empresaId == null || contactosSeleccionados.length === 0) {
      enqueueSnackbar('Seleccioná al menos un contacto', { variant: 'warning' });
      return;
    }
    setAsociando(true);
    try {
      const updated = await crmEmpresaService.asociarContactos(empresaId, {
        contactoIds: contactosSeleccionados.map((c) => c.id),
      });
      setDetalle(updated);
      setAsociarOpen(false);
      setContactosSeleccionados([]);
      setBusquedaContacto('');
      enqueueSnackbar('Contactos asociados', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al asociar contactos'), { variant: 'error' });
    } finally {
      setAsociando(false);
    }
  };

  const handleDesasociar = async (contactoId: number) => {
    if (empresaId == null) return;
    try {
      const updated = await crmEmpresaService.desasociarContacto(empresaId, contactoId);
      setDetalle(updated);
      enqueueSnackbar('Contacto desasociado', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al desasociar el contacto'), { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <Typography>Cargando...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={3}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(ROUTES.crm.empresas.list)}
                >
                  Volver
                </Button>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  {isNew ? 'Nueva empresa' : 'Editar empresa'}
                </Typography>
              </Stack>
              <Can permiso={isNew ? 'crm.empresas.nuevo' : 'crm.empresas.editar'}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  Guardar
                </Button>
              </Can>
            </Stack>

            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Nombre"
                  required
                  fullWidth
                  value={form.nombre}
                  onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                />
                <TextField
                  label="CUIT"
                  fullWidth
                  value={form.cuit}
                  onChange={(e) => setForm((f) => ({ ...f, cuit: e.target.value }))}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Teléfono"
                  fullWidth
                  value={form.telefono}
                  onChange={(e) => setForm((f) => ({ ...f, telefono: e.target.value }))}
                />
                <TextField
                  label="Email"
                  fullWidth
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
                <TextField
                  label="Sitio web"
                  fullWidth
                  value={form.sitioWeb}
                  onChange={(e) => setForm((f) => ({ ...f, sitioWeb: e.target.value }))}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
                <TextField
                  label="Dirección"
                  fullWidth
                  value={form.direccion}
                  onChange={(e) => setForm((f) => ({ ...f, direccion: e.target.value }))}
                />
                <TextField
                  label="N° Cliente"
                  fullWidth
                  value={form.nroCliente}
                  onChange={(e) => setForm((f) => ({ ...f, nroCliente: e.target.value }))}
                  helperText="Opcional: vincular con cliente existente"
                />
                {!isNew && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.activo}
                        onChange={(e) => setForm((f) => ({ ...f, activo: e.target.checked }))}
                      />
                    }
                    label="Activo"
                    sx={{ mt: 1 }}
                  />
                )}
              </Stack>
              <TextField
                label="Notas"
                fullWidth
                multiline
                minRows={2}
                value={form.notas}
                onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))}
              />
            </Stack>

            {!isNew && empresaId != null && (
              <>
                <Divider />
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  spacing={1}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Contactos asociados
                  </Typography>
                  <Can permiso="crm.empresas.editar">
                    <Button
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      onClick={() => {
                        setContactosSeleccionados([]);
                        setBusquedaContacto('');
                        setAsociarOpen(true);
                      }}
                    >
                      Asociar contactos
                    </Button>
                  </Can>
                </Stack>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Cargo</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Teléfono</TableCell>
                        <TableCell>Vendedor</TableCell>
                        <TableCell align="right">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(detalle?.contactos ?? []).map((c) => (
                        <TableRow key={c.id} hover>
                          <TableCell>
                            <Button
                              size="small"
                              onClick={() => navigate(ROUTES.crm.contactos.editar(c.id))}
                              sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                            >
                              {c.nombre}
                            </Button>
                          </TableCell>
                          <TableCell>{c.cargo || '-'}</TableCell>
                          <TableCell>{c.email || '-'}</TableCell>
                          <TableCell>{c.telefono || '-'}</TableCell>
                          <TableCell>{c.nombreVendedor || c.nroVendedor}</TableCell>
                          <TableCell align="right">
                            <Can permiso="crm.empresas.editar">
                              <IconButton
                                size="small"
                                color="error"
                                title="Desasociar"
                                onClick={() => handleDesasociar(c.id)}
                              >
                                <LinkOffIcon fontSize="small" />
                              </IconButton>
                            </Can>
                          </TableCell>
                        </TableRow>
                      ))}
                      {(detalle?.contactos?.length ?? 0) === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                              Todavía no hay contactos asociados a esta empresa.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={asociarOpen} onClose={() => setAsociarOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Asociar contactos</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              multiple
              options={contactoOptions}
              loading={loadingContactos}
              value={contactosSeleccionados}
              onChange={(_, value) => setContactosSeleccionados(value)}
              getOptionLabel={(opt) =>
                `${opt.nombre}${opt.email ? ` (${opt.email})` : ''}${opt.empresa ? ` — ${opt.empresa}` : ''}`
              }
              isOptionEqualToValue={(a, b) => a.id === b.id}
              filterOptions={(x) => x}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') setBusquedaContacto(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar contactos"
                  placeholder="Nombre, email..."
                  helperText="Los contactos ya asociados no aparecen en la lista"
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAsociarOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAsociar} disabled={asociando}>
            Asociar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrmEmpresaForm;
